/**
 * Error state displayed when a player search query fails.
 *
 * Rendered by the shared PlayerSearchResultsList and by the mobile Search
 * tab, so every player-search surface presents a failed query the same way.
 * Renders a warning icon, heading, and subtitle informing the user of the
 * failure.
 *
 * The copy deliberately says nothing about whether any player MATCHED. A
 * failed query knows nothing about the user's search, so stating a match
 * result would be a fact the app does not have (SE-7374, SE-7640).
 * @module components/shared/search-modal/SearchError/SearchErrorView
 */

import { AlertTriangle } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** testID for the retry action offered on the failure state */
export const SEARCH_ERROR_RETRY_TEST_ID = "player-search-error:retry";

/** Props for SearchErrorView */
interface SearchErrorViewProps {
  /**
   * Re-runs the failed query. When omitted the failure is presented without
   * an action — a surface that cannot actually re-run the query must not
   * render a button that does nothing (SE-7640).
   */
  readonly onRetry?: () => void;
}

/**
 * Centered error state with icon, heading, subtitle, and an optional retry.
 * @param props - Component props
 * @param props.onRetry - Re-runs the failed query; omit when the surface has
 *   no way to retry, so no dead button is rendered
 */
const SearchErrorView = ({ onRetry }: SearchErrorViewProps) => (
  <Stack className="items-center gap-4 px-6 pb-6 pt-10">
    <Box className="items-center justify-center rounded-full bg-needs-semantic-error-50 p-4">
      <Icon as={AlertTriangle} size="xl" className="text-status-error" />
    </Box>
    <Stack className="items-center gap-1">
      <Text variant="title-sm" className="text-content-primary">
        Something went wrong
      </Text>
      <Text variant="body" className="text-center text-content-muted">
        {onRetry
          ? "We couldn't load players. Please try again."
          : "Unable to load players. Please try again later."}
      </Text>
    </Stack>
    {onRetry ? (
      <Button
        label="Try again"
        tone="secondary"
        variant="outline"
        size="sm"
        onPress={onRetry}
        testID={SEARCH_ERROR_RETRY_TEST_ID}
      />
    ) : null}
  </Stack>
);

SearchErrorView.displayName = "SearchErrorView";

export default memo(SearchErrorView);
